import React from 'react';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import configs and util modules
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import {
  bookingDatesRequired,
  required,
  moneySubUnitAmountAtLeast,
} from '../../../../util/validators';

// Import shared components
import {
  Form,
  Button,
  FieldDateRangePicker,
  FieldSelect,
  FieldCurrencyInput,
  FieldTextInput,
  FieldCheckbox,
} from '../../../../components';

import {
  getStartOf,
  addTime,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  parseDateFromISO8601,
} from '../../../../util/dates';
import { types as sdkTypes } from '../../../../util/sdkLoader';
import appSettings from '../../../../config/settings';

import { formatMoney } from '../../../../util/currency';

// Import modules from this directory
import css from './EditListingAlertForm.module.css';

const { Money } = sdkTypes;

// Small line icons rendered inside each numbered step's badge. They inherit
// the badge's color via `currentColor` so they follow the marketplace accent.
const IconLocation = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s7-5.686 7-11a7 7 0 1 0-14 0c0 5.314 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.5" />
  </svg>
);

const IconCalendar = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
  </svg>
);

const IconGuests = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" />
  </svg>
);

const IconBudget = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 7c-1.5-1.5-3.5-2-6-2-3.5 0-6 1.5-6 4 0 4 12 2 12 6 0 2.5-2.5 4-6 4-2.5 0-4.5-.5-6-2" />
    <path d="M12 3v18" />
  </svg>
);

const IconSparkle = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 4.9L18.8 9.8l-4.9 1.9L12 16.6l-1.9-4.9L5.2 9.8l4.9-1.9L12 3z" />
    <path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8z" />
  </svg>
);

// A single numbered step: a rounded icon badge on the left and, on the right, a
// numbered heading followed by the step's field(s).
const Step = ({ number, icon, title, subtitle, children }) => (
  <div className={css.step}>
    <div className={css.stepIcon} aria-hidden="true">
      {icon}
    </div>
    <div className={css.stepBody}>
      <h3 className={css.stepTitle}>
        <span className={css.stepNumber}>{number}.</span> {title}
      </h3>
      {subtitle ? <p className={css.stepSubtitle}>{subtitle}</p> : null}
      {children}
    </div>
  </div>
);

const TODAY = new Date();
const GUEST_OPTIONS = Array.from({ length: 16 }, (_, i) => ({
  key: i + 1,
  label: `${i + 1} guest${i + 1 > 1 ? 's' : ''}`,
}));

const BEDROOM_OPTIONS = Array.from({ length: 8 }, (_, i) => ({
  key: i + 1,
  label: `${i + 1} bedroom${i + 1 > 1 ? 's' : ''}`,
}));

const BATHROOM_OPTIONS = Array.from({ length: 16 }, (_, i) => ({
  key: i + 1,
  label: `${i + 1} bathroom${i + 1 > 1 ? 's' : ''}`,
}));

const getExclusiveEndDate = (date, timeZone) => {
  return getStartOf(date, 'day', timeZone, 1, 'days');
};

const getStartAndEndOnTimeZone = (startDate, endDate, isDaily, timeZone) => {
  // Parse the startDate and endDate into the target time zone
  const parsedStart = startDate
    ? getStartOf(timeOfDayFromLocalToTimeZone(startDate, timeZone), 'day', timeZone)
    : startDate;

  const parsedEnd = endDate
    ? getStartOf(timeOfDayFromLocalToTimeZone(endDate, timeZone), 'day', timeZone)
    : endDate;

  // Adjust endDate for API if isDaily is true
  const endDateForAPI = parsedEnd && isDaily ? getExclusiveEndDate(parsedEnd, timeZone) : parsedEnd;

  // Return the processed dates
  return { startDate: parsedStart, endDate: endDateForAPI };
};

const getPriceValidators = (listingMinimumPriceSubUnits, marketplaceCurrency, intl) => {
  // Budget is optional: an empty value is always valid. When a configured
  // minimum exists, enforce it only once the traveller has actually entered an
  // amount (i.e. the value is a Money instance).
  if (!listingMinimumPriceSubUnits) {
    return undefined;
  }

  const minPriceRaw = new Money(listingMinimumPriceSubUnits, marketplaceCurrency);
  const minPrice = formatMoney(intl, minPriceRaw);
  const priceTooLowMsgId = { id: 'EditListingPricingForm.priceTooLow' };
  const priceTooLowMsg = intl.formatMessage(priceTooLowMsgId, { minPrice });
  const minPriceRequired = moneySubUnitAmountAtLeast(priceTooLowMsg, listingMinimumPriceSubUnits);

  return value => (value instanceof Money ? minPriceRequired(value) : undefined);
};

/**
 * The EditListingAlertForm component.
 *
 * @component
 * @param {Object} props
 * @param {string} props.formId - The form id
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {boolean} props.autoFocus - Whether the form is auto focused
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {boolean} props.ready - Whether the form is ready
 * @param {boolean} props.updated - Whether the form is updated
 * @param {boolean} props.updateInProgress - Whether the update is in progress
 * @param {Object} props.fetchErrors - The fetch errors object
 * @param {string} props.saveActionMsg - The save action message
 * @param {Function} props.onSubmit - The submit function
 * @param {Object} props.errors - The errors object
 * @param {propTypes.error} props.errors.showListingsError - The show listings error
 * @param {propTypes.error} props.errors.updateListingError - The update listing error
 * @returns {JSX.Element}
 */
export const EditListingAlertForm = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    keepDirtyOnReinitialize
    render={formRenderProps => {
      const {
        formId = 'EditListingAlertForm',
        autoFocus,
        className,
        rootClassName,
        disabled,
        ready,
        form,
        handleSubmit,
        invalid,
        pristine,
        saveActionMsg,
        updated,
        updateInProgress = false,
        fetchErrors,
        listingMinimumPriceSubUnits,
        marketplaceCurrency,
        locationOptions = [],
        values = {},
      } = formRenderProps;

      const intl = useIntl();
      const addressRequiredMessage = intl.formatMessage({
        id: 'EditListingAlertForm.addressRequired',
      });

      const { updateListingError, showListingsError } = fetchErrors || {};

      const classes = classNames(rootClassName || css.root, className);
      const submitReady = updated;
      const submitInProgress = updateInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;

      const timeZone = 'Europe/London';
      const dateFormatOptions = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      };

      const startDateErrorMessage = intl.formatMessage({
        id: 'FieldDateRangeInput.invalidStartDate',
      });
      const endDateErrorMessage = intl.formatMessage({
        id: 'FieldDateRangeInput.invalidEndDate',
      });

      // Dates and the flexible-dates options are alternatives: the traveller can
      // give exact dates OR pick a flexibility option. Dates are only required
      // when no flexibility option is selected; if both are empty we show the
      // required error on the date field. A partially entered range is always
      // validated for consistency (both ends must be set).
      const hasFlexibleOption =
        Array.isArray(values.flexibleDates) && values.flexibleDates.length > 0;
      const validateBookingDates = value => {
        const startSet = value?.startDate instanceof Date;
        const endSet = value?.endDate instanceof Date;
        if (!startSet && !endSet) {
          return hasFlexibleOption
            ? undefined
            : intl.formatMessage({ id: 'EditListingAlertForm.datesOrFlexibleRequired' });
        }
        return bookingDatesRequired(startDateErrorMessage, endDateErrorMessage)(value);
      };

      const startOfToday = getStartOf(TODAY, 'day', timeZone);
      const tomorrow = addTime(startOfToday, 1, 'days');
      const startDatePlaceholderText = intl.formatDate(startOfToday, dateFormatOptions);
      const endDatePlaceholderText = intl.formatDate(tomorrow, dateFormatOptions);

      const priceValidators = getPriceValidators(
        listingMinimumPriceSubUnits,
        marketplaceCurrency,
        intl
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          {updateListingError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingAlertForm.updateFailed" />
            </p>
          ) : null}

          {showListingsError ? (
            <p className={css.error}>
              <FormattedMessage id="EditListingAlertForm.showListingFailed" />
            </p>
          ) : null}

          <Step
            number={1}
            icon={<IconLocation />}
            title={intl.formatMessage({ id: 'EditListingAlertForm.step1Title' })}
          >
            {/* The step title is the field's heading, so the input carries its
                label only for assistive tech. */}
            <FieldSelect
              id="location"
              name="location"
              className={css.locationAddress}
              autoFocus={autoFocus}
              aria-label={intl.formatMessage({ id: 'EditListingAlertForm.address' })}
              validate={required(addressRequiredMessage)}
            >
              <option disabled value="">
                {intl.formatMessage({ id: 'EditListingAlertForm.addressPlaceholder' })}
              </option>
              {locationOptions.map(option => (
                <option key={option.option} value={option.option}>
                  {option.label}
                </option>
              ))}
            </FieldSelect>
          </Step>

          <Step
            number={2}
            icon={<IconCalendar />}
            title={intl.formatMessage({ id: 'EditListingAlertForm.step2Title' })}
          >
            <FieldDateRangePicker
                className={css.bookingDates}
                name="bookingDates"
                isDaily={false}
                startDateId="bookingStartDate"
                startDateLabel={intl.formatMessage({
                  id: 'EditListingAlertForm.bookingStartTitle',
                })}
                startDatePlaceholderText={startDatePlaceholderText}
                endDateId="bookingEndDate"
                endDateLabel={intl.formatMessage({
                  id: 'EditListingAlertForm.bookingEndTitle',
                })}
                endDatePlaceholderText={endDatePlaceholderText}
                format={v => {
                  const { startDate, endDate } = v || {};
                  // Format the Final Form field's value for the DateRangePicker
                  // DateRangePicker operates on local time zone, but the form uses listing's time zone
                  const formattedStart = startDate
                    ? timeOfDayFromTimeZoneToLocal(startDate, timeZone)
                    : startDate;
                  const endDateForPicker = endDate;
                  const formattedEnd = endDateForPicker
                    ? timeOfDayFromTimeZoneToLocal(endDateForPicker, timeZone)
                    : endDateForPicker;
                  return v ? { startDate: formattedStart, endDate: formattedEnd } : v;
                }}
                parse={v => {
                  const { startDate, endDate } = v || {};
                  return v ? getStartAndEndOnTimeZone(startDate, endDate, false, timeZone) : v;
                }}
                validate={validateBookingDates}
              />
            <div className={css.flexibleDates}>
              {/* The two options are mutually exclusive: checking one clears the
                  other (they still behave as toggles, so either can be unset). */}
              <FieldCheckbox
                id="flexible2to3"
                name="flexibleDates"
                value="flex_2_3"
                label={intl.formatMessage({ id: 'EditListingAlertForm.flexible2to3' })}
                onChange={e => {
                  if (e.target.checked) {
                    form.change('flexibleDates', ['flex_2_3']);
                  }
                }}
              />
              <FieldCheckbox
                id="flexibleUpTo7"
                name="flexibleDates"
                value="flex_up_to_7"
                label={intl.formatMessage({ id: 'EditListingAlertForm.flexibleUpTo7' })}
                onChange={e => {
                  if (e.target.checked) {
                    form.change('flexibleDates', ['flex_up_to_7']);
                  }
                }}
              />
            </div>
          </Step>

          <Step
            number={3}
            icon={<IconGuests />}
            title={intl.formatMessage({ id: 'EditListingAlertForm.step3Title' })}
          >
            <div className={css.requirements}>
                <FieldSelect
                  id="guests"
                  name="guests"
                  className={css.guests}
                  label={intl.formatMessage({ id: 'EditListingAlertForm.guests' })}
                  validate={required(
                    intl.formatMessage({
                      id: 'EditListingAlertForm.guestsRequired',
                    })
                  )}
                >
                  {GUEST_OPTIONS.map(option => {
                    return (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    );
                  })}
                </FieldSelect>

                <FieldSelect
                  id="bedrooms"
                  name="bedrooms"
                  className={css.bedrooms}
                  label={intl.formatMessage({ id: 'EditListingAlertForm.bedrooms' })}
                  validate={required(
                    intl.formatMessage({
                      id: 'EditListingAlertForm.bedroomsRequired',
                    })
                  )}
                >
                  {BEDROOM_OPTIONS.map(option => {
                    return (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    );
                  })}
                </FieldSelect>

                <FieldSelect
                  id="bathrooms"
                  name="bathrooms"
                  className={css.bathrooms}
                  label={intl.formatMessage({ id: 'EditListingAlertForm.bathrooms' })}
                  validate={required(
                    intl.formatMessage({
                      id: 'EditListingAlertForm.bathroomsRequired',
                    })
                  )}
                >
                  {BATHROOM_OPTIONS.map(option => {
                    return (
                      <option key={option.key} value={option.key}>
                        {option.label}
                      </option>
                    );
                  })}
                </FieldSelect>
              </div>
          </Step>

          <Step
            number={4}
            icon={<IconBudget />}
            title={intl.formatMessage({ id: 'EditListingAlertForm.step4Title' })}
          >
            <div className={css.priceFields}>
              <FieldCurrencyInput
                id="maxPrice"
                name="maxPrice"
                className={css.maxPrice}
                aria-label={intl.formatMessage({ id: 'EditListingAlertForm.maxPrice' })}
                placeholder={intl.formatMessage({
                  id: 'EditListingAlertForm.maxPricePlaceholder',
                })}
                currencyConfig={appSettings.getCurrencyFormatting(marketplaceCurrency)}
                validate={priceValidators}
              />
            </div>
          </Step>

          <Step
            number={5}
            icon={<IconSparkle />}
            title={intl.formatMessage({ id: 'EditListingAlertForm.step5Title' })}
          >
            <FieldTextInput
              id="perfectStay"
              name="perfectStay"
              type="textarea"
              className={css.perfectStay}
              placeholder={intl.formatMessage({
                id: 'EditListingAlertForm.perfectStayPlaceholder',
              })}
            />
          </Step>

          <div className={css.submitWrapper}>
            <Button
              className={css.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
              ready={submitReady && !submitDisabled}
            >
              {saveActionMsg}
            </Button>
          </div>
        </Form>
      );
    }}
  />
);

export default EditListingAlertForm;
